import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { CheckboxField, Dropdown, TextField } from '@app/components';

import {
  academicInstitutionItems,
  academicStatusItems,
  ageTypeItems,
  dropdownOptionScheme,
  phoneTypeItems,
  PreRegistrationAcademicStatus,
  PreRegistrationUseType,
  useTypeItems,
} from './constants';

import './PreregistrationForm.scss';

const cnBem = createBemBlockBuilder(['preregistrationForm']);
const cnBemDropdown = createBemBlockBuilder(['preregistrationFormDropdown']);

export const PreregistrationForm = () => {
  const { $t } = useIntl();
  const { handleSubmit, control } = useForm({
    mode: 'onTouched',
  });

  const [phoneNumberType, setPhoneNumberType] = useState(phoneTypeItems[0]);
  const [ageType, setAgeType] = useState(dropdownOptionScheme);
  const [useType, setUseType] = useState(dropdownOptionScheme);
  const [academicStatus, setAcademicStatus] = useState(dropdownOptionScheme);
  const [academicInstitution, setAcademicInstitution] =
    useState(dropdownOptionScheme);
  const isAcademicInstitutionDisplayed =
    academicStatus.value &&
    academicStatus.value !== PreRegistrationAcademicStatus.highSchool;

  return (
    <form
      data-auto="preregistration-form"
      onSubmit={handleSubmit(values => {
        // eslint-disable-next-line no-console
        console.log({
          ...values,
          phoneType: phoneNumberType.value,
          useType: useType.value,
          ageType: ageType.value,
          academicStatus: academicStatus.value,
          academicInstitution: academicInstitution.value,
        });
      })}
    >
      <h2 className={cnBem('__title')}>
        {$t({
          id: 'preregistration.formTitle',
          defaultMessage: 'Preregister for a Reader Identification Card',
        })}
      </h2>
      <span className={cnBem('__sectionTitle')}>
        {$t({
          id: 'preregistration.address',
          defaultMessage: 'Address',
        })}
      </span>
      <div className={cnBem('__row')}>
        <TextField
          name="currentAddress"
          label={$t({
            id: 'preregistration.currentAddress',
            defaultMessage: 'Current address',
          })}
          control={control}
        />
      </div>
      <div className={cnBem('__row')}>
        <TextField
          name="city"
          label={$t({
            id: 'preregistration.city',
            defaultMessage: 'City',
          })}
          control={control}
        />
      </div>
      <div className={cnBem('__row')}>
        <TextField
          name="postalCode"
          label={$t({
            id: 'preregistration.postalCode',
            defaultMessage: 'Zip/Postal code',
          })}
          control={control}
        />
      </div>
      <div className={cnBem('__row')}>
        <TextField
          name="country"
          label={$t({
            id: 'preregistration.country',
            defaultMessage: 'Country',
          })}
          control={control}
        />
      </div>
      <div className={cnBem('__divider')} />
      <span className={cnBem('__sectionTitle')}>
        {$t({
          id: 'preregistration.addressOnPhotoId',
          defaultMessage: 'Address on photo ID',
        })}
      </span>
      <CheckboxField
        className={cnBem('__checkbox')}
        name="isSameWithCurrentAddress"
        label={$t({
          id: 'preregistration.checkboxLabel',
          defaultMessage: 'Same as current address',
        })}
        defaultValue={false}
        control={control}
      />
      <div className={cnBem('__divider')} />
      <span className={cnBem('__sectionTitle')}>
        {$t({
          id: 'preregistration.phone',
          defaultMessage: 'Phone',
        })}
      </span>
      <div className={cnBem('__rowInline')}>
        <Dropdown
          id="items-per-page-dropdown"
          className={cnBemDropdown('__phone')}
          label={$t({
            id: 'preregistration.type',
            defaultMessage: 'Type',
          })}
          styleType="standard"
          items={phoneTypeItems}
          selectedItem={phoneNumberType}
          onSelectedItemChange={option =>
            setPhoneNumberType(oldValue => ({
              ...oldValue,
              ...option.selectedItem,
            }))
          }
        />
        <TextField
          label={$t({
            id: 'preregistration.phoneNumber',
            defaultMessage: 'Phone number',
          })}
          name="phoneNumber"
          control={control}
          className={cnBem('__phoneInput')}
        />
      </div>
      <div className={cnBem('__divider')} />
      <span className={cnBem('__sectionTitle')}>
        {$t({
          id: 'preregistration.userType',
          defaultMessage: 'User type',
        })}
      </span>
      <Dropdown
        id="age-type-selection"
        className={cnBemDropdown('__item')}
        label={$t({
          id: 'preregistration.age',
          defaultMessage: 'Age',
        })}
        styleType="standard"
        items={ageTypeItems}
        selectedItem={ageType}
        onSelectedItemChange={option =>
          setAgeType(oldValue => ({
            ...oldValue,
            ...option.selectedItem,
          }))
        }
      />
      <Dropdown
        id="usage-type-selection"
        className={cnBemDropdown('__item')}
        label={$t({
          id: 'preregistration.use',
          defaultMessage: 'Use',
        })}
        styleType="standard"
        items={useTypeItems}
        selectedItem={useType}
        onSelectedItemChange={option => {
          setUseType(oldValue => ({
            ...oldValue,
            ...option.selectedItem,
          }));
          setAcademicStatus(dropdownOptionScheme);
          setAcademicInstitution(dropdownOptionScheme);
        }}
      />
      {useType.value === PreRegistrationUseType.academic && (
        <Dropdown
          id="academic-status-selection"
          className={cnBemDropdown('__item')}
          label={$t({
            id: 'preregistration.academicStatus',
            defaultMessage: 'Academic status',
          })}
          styleType="standard"
          items={academicStatusItems}
          selectedItem={academicStatus}
          onSelectedItemChange={option =>
            setAcademicStatus(oldValue => ({
              ...oldValue,
              ...option.selectedItem,
            }))
          }
        />
      )}
      {isAcademicInstitutionDisplayed && (
        <Dropdown
          id="academic-institution-selection"
          className={cnBemDropdown('__item')}
          label={$t({
            id: 'preregistration.academicInstitution',
            defaultMessage: 'Academic institution',
          })}
          styleType="standard"
          items={academicInstitutionItems}
          selectedItem={academicInstitution}
          onSelectedItemChange={option =>
            setAcademicInstitution(oldValue => ({
              ...oldValue,
              ...option.selectedItem,
            }))
          }
        />
      )}
      <div className={cnBem('__divider')} />
      <CheckboxField
        className={cnBem('__agreementCheckbox')}
        name="agreementLegalStatement"
        label={$t(
          {
            id: 'preregistration.agreementLegalStatement',
            defaultMessage:
              'I have read and understand the Library of Congress’ <link>Legal Statements</link>.',
          },
          {
            link: chunks => <a href="/">{chunks}</a>,
          }
        )}
        defaultValue={false}
        control={control}
      />
      <CheckboxField
        className={cnBem('__agreementCheckbox')}
        name="agreementReceiveEmails"
        label={$t({
          id: 'preregistration.agreementReceiveEmails',
          defaultMessage:
            'I would like to receive email communications from the Library of Congress about service requests and alerts, research information, and surveys related to doing research at the Library.',
        })}
        defaultValue={false}
        control={control}
      />
      <CheckboxField
        className={cnBem('__agreementCheckbox')}
        name="agreementReceiveUpdates"
        label={$t({
          id: 'preregistration.agreementReceiveUpdates',
          defaultMessage:
            'I would like to receive email communications from the Library of Congress about other Library programs (concerts, exhibits, lectures, etc.), customer surveys, and other services available to the general public.',
        })}
        defaultValue={false}
        control={control}
      />
      <CheckboxField
        className={cnBem('__agreementCheckbox')}
        name="agreementSupportEmails"
        label={$t({
          id: 'preregistration.agreementSupportEmails',
          defaultMessage:
            'I would like to receive email communications from the Library of Congress about how I might support the Library.',
        })}
        defaultValue={false}
        control={control}
      />
      <div className={cnBem('__row')}>
        <Button
          className={cnBem('__submitButton')}
          styleType="solid"
          type="submit"
        >
          {$t({
            id: 'preregistration.title',
            defaultMessage: 'Preregister',
          })}
        </Button>
      </div>
    </form>
  );
};
